/**
 * Sync Error Toast Utilities
 *
 * Maps SyncError objects to ToastShape (decoupled from component types).
 * This module follows the parametric design pattern - it only depends on
 * utility-layer types and functions, not component-layer types.
 *
 * The ToastShape is converted to component ToastOptions via toastAdapter.ts
 * in the component layer.
 *
 * @module syncErrorToast
 */

import type { SyncError } from '@/types/sync';
import type { SchemaVersionError } from '@/types/sync/SyncProvider';
import { getSyncErrorMessage, deriveErrorCategory } from './syncErrorMessages';
import { getProviderDisplayName } from './syncProviderUtils';

/**
 * Toast color variants for UI presentation.
 *
 * Maps to HeroUI toast colors but defined in utils layer to avoid
 * coupling to component implementation.
 */
export type ToastColor = 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger';

/**
 * Action types for toast buttons/links.
 *
 * These are discriminators that the component-layer adapter maps to
 * actual React components (buttons, links, etc).
 *
 * - 'refresh': Refresh button that calls window.location.reload()
 * - 'settings-link': Link to /settings/data-sync page (Phase 4B2)
 */
export type ToastActionType = 'refresh' | 'settings-link';

/**
 * Toast shape - plain object representation of toast data.
 *
 * This is a utility-layer type that is independent of component implementation.
 * Use toastAdapter.ts to convert ToastShape → component ToastOptions.
 */
export interface ToastShape {
  /** Toast title */
  title: string;
  /** Toast description */
  description: string;
  /** Toast color variant */
  color: ToastColor;
  /** Auto-dismiss timeout in milliseconds */
  timeout?: number;
  /**
   * Action type for toast button/link.
   * The component-layer adapter maps this to actual React components.
   * Optional - toasts without actions won't have endContent.
   */
  actionType?: ToastActionType;
}

/**
 * Timeout durations for toast auto-dismiss based on error category.
 * @constant
 */
const TOAST_TIMEOUT_MS = {
  /** Permanent/recoverable errors require user action - longer timeout for reading */
  PERMANENT: 8000,
  /** Transient errors are auto-retry/informational - shorter timeout */
  TRANSIENT: 5000,
} as const;

/**
 * Builds a ToastShape from a SyncError for display in toast notifications.
 *
 * **Design Notes**:
 * - Returns plain object (ToastShape), not component-specific type
 * - Uses getSyncErrorMessage() for consistent messaging across contexts
 * - Maps error severity to toast color and timeout
 * - Uses deriveErrorCategory() to determine timeout duration
 *
 * @param error - The SyncError to convert to toast shape
 * @returns ToastShape ready for conversion to component ToastOptions via adapter
 *
 * @example
 * ```typescript
 * const error: SyncError = {
 *   code: 'NOT_CONNECTED',
 *   message: 'No tokens found',
 *   provider: 'google-drive'
 * };
 *
 * const toastShape = buildSyncErrorToastShape(error);
 * // Returns: {
 * //   title: 'Google Drive Connection Required',
 * //   description: 'Sign in to Google Drive to enable sync.',
 * //   color: 'danger',
 * //   timeout: 8000
 * // }
 *
 * // In component layer, convert via adapter:
 * import { toastShapeToOptions } from '@/components/shared/Toast';
 * const toastOptions = toastShapeToOptions(toastShape);
 * ```
 */
export function buildSyncErrorToastShape(error: SyncError): ToastShape {
  // Get provider display name for user-friendly messages
  const providerDisplayName = getProviderDisplayName(error.provider);

  // Get error message optimized for toast context (brief)
  const errorMessage = getSyncErrorMessage(error, 'toast', providerDisplayName);

  // Determine error category for timeout calculation
  const category = deriveErrorCategory(error);

  // Map error code to toast color (severity-based)
  const color: ToastColor = mapErrorCodeToColor(error.code);

  // Set timeout based on error category
  // Both permanent and recoverable errors require user action, so use longer timeout
  // Only transient errors (auto-retry) use shorter timeout
  const timeout =
    category === 'transient' ? TOAST_TIMEOUT_MS.TRANSIENT : TOAST_TIMEOUT_MS.PERMANENT;

  return {
    title: errorMessage.title,
    description: errorMessage.description,
    color,
    timeout,
  };
}

/**
 * Maps SyncErrorCode to ToastColor based on severity.
 *
 * **Mapping Logic**:
 * - Errors requiring immediate user action → 'danger'
 * - Transient/auto-retry errors → 'warning'
 *
 * @param code - The SyncErrorCode to map
 * @returns Appropriate ToastColor for the error
 */
function mapErrorCodeToColor(code: string): ToastColor {
  switch (code) {
    case 'NOT_CONNECTED':
    case 'PERMISSION_DENIED':
      // Auth errors - immediate user action required
      return 'danger';

    case 'FILE_NOT_FOUND':
    case 'QUOTA_EXCEEDED':
    case 'INVALID_FILE':
      // Data errors - user intervention needed
      return 'danger';

    case 'NETWORK_ERROR':
    case 'CONFLICT_DETECTED':
      // Transient/recoverable - system will auto-retry or guide resolution
      return 'warning';

    case 'UNKNOWN_ERROR':
      // Unknown state - treat as critical
      return 'danger';

    default:
      // Fallback for any new error codes
      return 'danger';
  }
}

/**
 * Schema version error timeout - longer than normal since these require user action
 * @constant
 */
const SCHEMA_VERSION_ERROR_TIMEOUT_MS = 10000;

/**
 * Builds a ToastShape from a SchemaVersionError for display in toast notifications.
 *
 * **Phase 4B1**: Only `SCHEMA_VERSION_NEWER` gets an actionType ('refresh').
 * **Phase 4B2**: MISSING/INVALID/MISMATCH will get 'settings-link' actionType.
 *
 * @param error - The SchemaVersionError to convert to toast shape
 * @returns ToastShape ready for conversion to component ToastOptions via adapter
 *
 * @example
 * ```typescript
 * const error: SchemaVersionError = {
 *   code: 'SCHEMA_VERSION_NEWER',
 *   message: 'Sync paused - please refresh',
 *   localVersion: 1,
 *   remoteVersion: 2,
 *   artifactType: 'baseline',
 *   artifactName: 'baseline-2025-01-01.json'
 * };
 *
 * const toastShape = buildSchemaVersionErrorToastShape(error);
 * // Returns: {
 * //   title: 'Sync Paused',
 * //   description: 'Please refresh your browser to continue syncing.',
 * //   color: 'warning',
 * //   timeout: 10000,
 * //   actionType: 'refresh'
 * // }
 * ```
 */
export function buildSchemaVersionErrorToastShape(error: SchemaVersionError): ToastShape {
  switch (error.code) {
    case 'SCHEMA_VERSION_NEWER':
      // Stale browser - user needs to refresh to get latest app code
      return {
        title: 'Sync Paused',
        description: 'Please refresh your browser to continue syncing.',
        color: 'warning',
        timeout: SCHEMA_VERSION_ERROR_TIMEOUT_MS,
        actionType: 'refresh',
      };

    case 'SCHEMA_VERSION_MISSING':
    case 'SCHEMA_VERSION_INVALID':
    case 'SCHEMA_VERSION_MISMATCH':
      // Corrupted cloud data - user needs to reset sync in Settings
      return {
        title: 'Sync Error',
        description: 'Cloud data is corrupted. Reset sync in Settings to fix.',
        color: 'danger',
        timeout: SCHEMA_VERSION_ERROR_TIMEOUT_MS,
        actionType: 'settings-link',
      };

    default:
      // Fallback for any unknown schema version error codes
      return {
        title: 'Sync Error',
        description: error.message || 'An unexpected sync error occurred.',
        color: 'danger',
        timeout: SCHEMA_VERSION_ERROR_TIMEOUT_MS,
      };
  }
}
